/**
 * Client-side donation state — wraps the shared drive math (@shared/donate)
 * with the per-browser bits the SPA banner needs:
 *
 *   - getDrive(): the drive for this browser — the localStorage override
 *     (Keys.donateDrive) when present, else the deterministic curve.
 *   - onFundingState(): a tiny pub-sub so the sticky banner re-renders in
 *     place when the state changes (e.g. another tab edited the override).
 *   - syncDriveFromApi(): the stale-while-revalidate hook the banner calls
 *     on mount. With a funding Worker URL this would fetch the canonical
 *     state; today it is a cheap local revalidation that also picks up
 *     cross-tab edits via the `storage` event.
 *
 * No module-level `window` access — safe to import in server-rendered
 * contexts (the /view page renders its own chip from @shared/donate).
 */
import { Keys } from "@/settings";
// noinspection ES6PreferShortImport
import { computeDrive } from "@shared/donate";
import type { FundingState } from "@shared/donate";

export { SUGGESTED_DONATION, formatDeadline, payPalDonateUrl, usd } from "@shared/donate";
export type { FundingState } from "@shared/donate";

type Listener = (state: FundingState) => void;
const listeners = new Set<Listener>();
let cached: FundingState | null = null;

function readOverride(): FundingState | null {
  try {
    const raw = window.localStorage.getItem(Keys.donateDrive);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (
      p &&
      typeof p === "object" &&
      Number.isFinite(p.target) &&
      Number.isFinite(p.raised) &&
      Number.isFinite(p.deadline)
    ) {
      return { target: p.target, raised: p.raised, deadline: p.deadline };
    }
  } catch {
    /* malformed override — fall through to the derived curve */
  }
  return null;
}

/** The stable, persisted drive for this browser. */
export function getDrive(): FundingState {
  if (!cached) cached = readOverride() ?? computeDrive();
  return cached;
}

function emit(): void {
  cached = null;
  const state = getDrive();
  for (const fn of listeners) fn(state);
}

let storageWired = false;
function wireStorageEvents(): void {
  if (storageWired || typeof window === "undefined") return;
  storageWired = true;
  window.addEventListener("storage", (e) => {
    // e.key === null means clearAll — also worth re-reading.
    if (e.key === Keys.donateDrive || e.key === null) emit();
  });
}

/**
 * Revalidate the drive. Reserved for a future funding Worker (the banner
 * passes the URL it would live at); today it re-reads the local override
 * and notifies subscribers.
 */
export function syncDriveFromApi(_apiUrl?: string): void {
  wireStorageEvents();
  emit();
}

/** Subscribe to drive updates; returns an unsubscribe function. */
export function onFundingState(fn: Listener): () => void {
  wireStorageEvents();
  listeners.add(fn);
  return () => void listeners.delete(fn);
}
